/* eslint-disable import/no-cycle */
/* eslint class-methods-use-this: ["error", { "exceptMethods": ["languageFromPerson", "languageFromBrowser", "anonymousLocaleForCMS", "resolveLocaleForCMS"],  }] */
import { action, makeObservable, observable } from 'mobx';
import { RichTextContent } from '@graphcms/rich-text-types';
import BaseStore from '../base-stores/BaseStore';
import { RootStore } from '../RootStore';
import { ContentService, LanguageType } from '../../swagger/api';
import {
  getStoredPublicCmsLocaleString,
  parsePublicLangFromSearch,
  setStoredPublicLang
} from '../../utils/publicLocale';

export interface MinKontoContent {
  systemId: string;
  content: string;
  subText: string | null;
  tooltip: string | null;
  richContent: MinKontoRichContent;
}

export interface MinKontoRichContent {
  raw?: RichTextContent;
  html?: string;
  markdown?: string;
  text?: string;
}

export interface MinKontoErrorModel {
  errorCode: string;
  errorMessage: string;
}

export interface Data {
  minKontoErrorModels?: MinKontoErrorModel[];
  minKontoContents?: MinKontoContent[];
}

export interface RootObject {
  data: Data;
}

export enum Locale {
  Danish = 'da_DK',
  English = 'en_GB'
}

export default class CMSStore extends BaseStore {
  content?: MinKontoContent[];

  errorContent?: MinKontoErrorModel[];

  languageFromPerson = () =>
    this.rootStore.userStore.person?.language === LanguageType.Danish
      ? Locale.Danish
      : Locale.English;

  localeFromPerson = () =>
    this.rootStore.userStore.person?.language === LanguageType.Danish ? 'da-DK' : 'en-US';

  languageFromBrowser = (): Locale => {
    const defaultLanguages = ['da', 'da-DK'];
    if (defaultLanguages.includes(window.navigator.language)) {
      return Locale.Danish;
    }
    return Locale.English;
  };

  /**
   * Anonymous: `?lang=` in URL (shareable) wins; else last choice in localStorage; else browser language.
   */
  anonymousLocaleForCMS = (): Locale => {
    const fromQuery = parsePublicLangFromSearch(window.location.search);
    if (fromQuery) {
      setStoredPublicLang(fromQuery === Locale.Danish ? 'da' : 'en');
      return fromQuery as Locale;
    }
    const fromStorage = getStoredPublicCmsLocaleString();
    if (fromStorage) {
      return fromStorage as Locale;
    }
    return this.languageFromBrowser();
  };

  resolveLocaleForCMS = (): Locale =>
    this.rootStore.userStore.person ? this.languageFromPerson() : this.anonymousLocaleForCMS();

  getCMSLocale = () => this.resolveLocaleForCMS();

  getCMSContent = () => {
    const locale = this.resolveLocaleForCMS();
    const getContentEndpoint = this.rootStore.manageUserAccessStore.isPrivateCustomer()
      ? ContentService.getContent
      : ContentService.getCommercialContent;
    const contentService = getContentEndpoint({ locale });
    contentService.then((data) => {
      const parsedData: RootObject = JSON.parse(data);
      this.content = parsedData.data.minKontoContents;
      if (this.rootStore.authenticationStore.isAuthenticated) {
        this.setInitialContent();
      }
    });
    return contentService;
  };

  getCMSErrorContent = () => {
    const locale = this.resolveLocaleForCMS();
    const contentService = ContentService.getBusinessErrorContent({ locale });
    contentService.then((data) => {
      const parsedData: RootObject = JSON.parse(data);
      this.errorContent = parsedData.data.minKontoErrorModels;
    });
    return contentService;
  };

  getContent = (contentId: string) => {
    const contentString: MinKontoContent | undefined = this.content?.find(
      (content) => content.systemId === contentId
    );
    return contentString;
  };

  getContentString = (contentId: string, params: [string, string][] = []) => {
    const content: MinKontoContent | undefined = this.content?.find(
      (c) => c.systemId === contentId
    );
    let contentString = content?.content;

    params.forEach((param) => {
      const [key, value] = param;
      contentString = contentString?.replaceAll(key, value);
    });

    return contentString;
  };

  getErrorMessage = (errorMessageKey: string) => {
    const errorMessage: MinKontoErrorModel | undefined = this.errorContent?.find(
      (error) => error.errorCode === errorMessageKey
    );
    return errorMessage?.errorMessage;
  };

  setInitialContent = () => {
    this.rootStore.contentStore.contentInitializerStore.setEuronormInit();
    this.rootStore.contentStore.contentInitializerStore.setInvoiceFilterContentInit();
    Promise.all([
      this.rootStore.contentStore.contentInitializerStore.getAndSetAllCountries(),
      this.rootStore.contentStore.contentInitializerStore.getAndSetLocalServices()
    ]).then(() => {
      this.rootStore.contentStore.contentInitializerStore.setProductTableColumnNamesInit();
      this.rootStore.contentStore.contentInitializerStore.setTransactionsFilterContentInit();
      this.rootStore.contentStore.contentInitializerStore.setOtherTransactionsFilterContent();
      this.rootStore.contentStore.contentInitializerStore.setPaymentCardsFilterContent();
      this.rootStore.contentStore.contentInitializerStore.setBizzesToConvertFilterInit();
      this.rootStore.contentStore.contentInitializerStore.setCardPaymentFilterContentInit();
      this.rootStore.contentStore.contentInitializerStore.setProductFilterInit();
    });
  };

  constructor(rootStore: RootStore) {
    super(rootStore);
    makeObservable(this, {
      content: observable,
      errorContent: observable,
      getCMSLocale: action,
      getCMSContent: action,
      getContent: action,
      getCMSErrorContent: action,
      getErrorMessage: action,
      setInitialContent: action,
      resolveLocaleForCMS: action,
      anonymousLocaleForCMS: action
    });
  }
}
